// Open invitations (User Management): invite someone, give them a new code when
// theirs expired, got lost or locked, or revoke it. A code is shown once, right
// after it's issued — the page shows it (InvitationCodeNotice) via onIssued.
import React, { useCallback, useEffect, useState } from 'react';
import { formatExpiry } from './InvitationCodeNotice';
import {
  listInvitations, createInvitation, reissueInvitation, revokeInvitation, apiError,
} from './invitationsApi';

const STATUS_LABELS = {
  pending: 'Waiting for sign-up',
  expired: 'Expired',
  locked: 'Locked (too many wrong tries)',
};
// Dot colour per state (.um-status--* in UserManagement.css).
const STATUS_TONES = { pending: 'wait', expired: 'off', locked: 'bad' };

const TYPE_LABELS = { invite: 'Invitation', reset: 'Password reset' };

export default function InvitationsPanel({ version = 0, onIssued, query = '' }) {
  const [rows, setRows] = useState([]);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setRows(await listInvitations());
      setError('');
    } catch (err) {
      setError(apiError(err));
    }
  }, []);

  useEffect(() => { load(); }, [load, version]);

  const act = async (work) => {
    setBusy(true);
    setError('');
    try {
      const invitation = await work();
      if (invitation) onIssued?.(invitation);
      await load();
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const invite = (event) => {
    event.preventDefault();
    const address = email.trim();
    if (!address) return;
    act(async () => {
      const invitation = await createInvitation(address);
      setEmail('');
      return invitation;
    });
  };

  const revoke = (row) => {
    if (!window.confirm(`Revoke the invitation for ${row.email}? Their code stops working, but their `
      + 'account stays. To remove them, use Delete under People.')) return;
    act(async () => { await revokeInvitation(row.id); return null; });
  };

  // The page's search box: matches the person's email or who issued the code.
  const q = query.trim().toLowerCase();
  const shown = q
    ? rows.filter((row) => row.email.toLowerCase().includes(q) || (row.created_by || '').toLowerCase().includes(q))
    : rows;

  return (
    <section className="um-panel" aria-label="Invitations">
      <div className="um-panel__head">
        <h2 className="um-panel__title">Invitations</h2>
        {rows.length > 0 && <span className="um-count">{rows.length} open</span>}
      </div>
      <p className="um-panel__hint">People who haven't finished signing up. Each code works once and expires after 7 days.</p>
      <form className="um-invite-form" onSubmit={invite}>
        <input type="email" className="um-field" aria-label="Email to invite"
          placeholder="Work email of the person to invite"
          value={email} onChange={(e) => setEmail(e.target.value)} disabled={busy} />
        <button type="submit" className="um-btn um-btn--primary" disabled={busy}>Invite</button>
      </form>
      {error && <div className="um-error" role="alert">{error}</div>}
      {rows.length === 0 || shown.length === 0 ? (
        <p className="um-empty">{rows.length === 0 ? 'No open invitations.' : 'No open invitations match your search.'}</p>
      ) : (
        <div className="um-table-wrap">
          <table className="um-table">
            <thead>
              <tr>
                <th>Email</th><th>Type</th><th>Status</th><th>Invited by</th><th>Expires</th><th>Emailed</th>
                <th className="um-table__actions-head">Actions</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((row) => (
                <tr key={row.id}>
                  <td className="um-strong">{row.email}</td>
                  <td>{TYPE_LABELS[row.purpose] || TYPE_LABELS.invite}</td>
                  <td>
                    <span className={`um-status um-status--${STATUS_TONES[row.status] || 'wait'}`}>
                      {STATUS_LABELS[row.status] || row.status}
                    </span>
                  </td>
                  <td className="um-muted">{row.created_by}</td>
                  <td>{formatExpiry(row.expires_at)}</td>
                  <td>{row.email_sent ? 'Yes' : 'No'}</td>
                  <td>
                    <div className="um-actions">
                      <button type="button" className="um-btn um-btn--outline" disabled={busy}
                        onClick={() => act(() => reissueInvitation(row.id))}>New code</button>
                      <button type="button" className="um-btn um-btn--danger" disabled={busy}
                        onClick={() => revoke(row)}>Revoke</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
